/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import Select from 'components/Select';
import { onError, onSuccess } from 'utils/handlers';
import { getEstadosGlobales } from 'api/catalogos';
import Table from 'components/Table';
import { ESTATUS_ACTIVO, ESTATUS_INACTIVO } from 'utils/constants';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalDelete from 'components/ModalDelete';
import FormSubmitControls from 'components/FormSubmitControls';
import {
  autorizarConfiguracionDePolizaCargaExterna,
  deleteConfiguracionDePolizaCargaExterna,
  getConfiguracionDePolizaCargaExterna,
  getConfiguracionesDePolizasDeCargaExterna,
  patchConfiguracionDePolizaCargaExterna,
  postConfiguracionDePolizaCargaExterna,
} from 'api/nominas/configuracionesDePolizasDeCargaExterna';
import { formatReceived } from 'utils/formatValues';
import moment from 'moment/moment';
import PolizaCargaExternaTabla from './PolizaCargaExternaTabla';

export const configuradorPolizasCargaExternaPermission = {
  permissionModel: 'configuraciondepolizacargaexterna',
  catalogsModels: [],
  tabsModels: [],
};

const ConfiguradorPolizasCargaExterna = ({ permission }) => {
  const [form] = Form.useForm();
  const [isAdding, setAdding] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguracionesDePolizasDeCargaExterna();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _estados] = await Promise.all([
        getConfiguracionesDePolizasDeCargaExterna(),
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setEstados(_estados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setAdding(false);
    const [key] = selectedRowKeys;
    const match = responseData || data?.find((item) => item.id === key);
    const _item = await getConfiguracionDePolizaCargaExterna(match.id);

    setTimeout(() => {
      if (_item) {
        const formValues = {
          ...formatReceived(_item),
          vigencia_final: _item.vigencia_final
            ? moment(_item.vigencia_final)
            : null,
        };
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formValues);
          setDisableFields(_item?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickAdd = () => {
    setAdding(true);
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();

      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      if (values.vigencia_final?.format) {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      }
      const saved = await (key
        ? patchConfiguracionDePolizaCargaExterna(key, values)
        : postConfiguracionDePolizaCargaExterna(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteConfiguracionDePolizaCargaExterna(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });

    const dataForForm = {
      ...record,
      vigencia_final: record.vigencia_final
        ? moment(record.vigencia_final)
        : null,
    };

    form.setFieldsValue(dataForForm);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una configuración que no está seleccionada');
        return;
      }
      const saved = await autorizarConfiguracionDePolizaCargaExterna(key);
      if (saved) {
        const _data = await getConfiguracionesDePolizasDeCargaExterna();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 200,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 180,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  return (
    <Row align="center" justify="center" className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!open ? (
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit: () => onClickEdit(),
                onClickDelete,
              }}
              customActions={[
                {
                  onClick: () => setVisibleAutorizar(true),
                  permission: { model: 'configuraciondepolizacargaexterna', action: 'autorizar' },
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  disabledBySelection: true,
                  disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
                  className: 'success',
                },
              ]}
              disableDelete={selectedData?.estados_globales !== ESTATUS_ACTIVO}
              permission={permission}
              allowExportParam={false}
              allowSearch={false}
            />
          ) : (
            <div className="form-container">
              <FormSubmitControls
                label={`${isAdding ? 'Agregar' : 'Editar'} Configuración de Póliza de Carga Externa`}
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                allowSave={!disableFields}
              />
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                scrollToFirstError
                name="configuracion_poliza_carga_externa_form"
                initialValues={{
                  estados_globales: ESTATUS_ACTIVO,
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="concepto"
                      label="Concepto"
                      rules={[requiredRule]}
                      hasFeedback
                    >
                      <Input allowClear disabled={disableFields} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="vigencia_final"
                      label="Vigencia Final"
                      hasFeedback
                    >
                      <DatePicker
                        allowClear
                        placeholder=""
                        format="YYYY-MM-DD"
                        disabled={isAdding || selectedData?.estados_globales === ESTATUS_INACTIVO}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      hasFeedback
                    >
                      <Select
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={estados}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>

              <Col xs={24} sm={24} md={24}>
                <PolizaCargaExternaTabla
                  configuracionDePolizaCargaExternaId={selectedData?.id}
                  disabled={disableFields}
                  permission={permission}
                />
              </Col>
            </div>
          )}

          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            open={visibleAlert}
            content="la configuración de póliza de carga externa seleccionada"
            loading={loading}
          />

          <ModalConfirmation
            onFinish={onConfirmAutorizar}
            ConfirmAutorize
            onCancel={() => setVisibleAutorizar(false)}
            open={visibleAutorizar}
            contentTitle={`autorizar la configuración de póliza de carga externa "${selectedData?.concepto || ''}"`}
            loading={loading}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default ConfiguradorPolizasCargaExterna;
